/**
 * Status Dashboard Component
 *
 * Migrated from: lego/page/etmf/overview/Status.jsx (1,727 lines)
 *
 * Main dashboard displaying ETMF document completion status across 4 hierarchy levels:
 * - All Levels (Product/Trial/Environment)
 * - Study Level (Trial-specific)
 * - Country Level
 * - Site Level
 *
 * MIGRATION STRATEGY: Incremental approach
 * Phase 2A: Basic structure + filter state (THIS FILE)
 * Phase 2B: Data fetching logic
 * Phase 2C: Filter cascades
 * Phase 2D: Integration with StudyDocuments
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigateWithParams } from '../../../hooks/useNavigateWithParams'
import { useServiceContext } from '../../../root'
import cloneDeep from 'lodash/cloneDeep'
import StatusGraph from './status-graph/StatusGraph'
import { Select } from '../../../components/Select/Select'
import StudyDocuments from './study-documents/StudyDocuments'
import type { GraphData, StatusFilter, DocumentStatus, StudyDocumentsFilterData } from './types'

// Simple label component to replace ControlLabel (react-bootstrap v2 doesn't have it)
const ControlLabel: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <label className={`control-label ${className || ''}`}>{children}</label>
)

// Constants for hierarchy levels
const LEVEL = {
  all: 'allLevels',
  trial: 'studyLevel',
  country: 'countryLevel',
  site: 'siteLevel'
} as const

const sections = Object.values(LEVEL)

// Property names used in filters
const propsLow = {
  product: 'product',
  trial: 'trial',
  environment: 'environment',
  country: 'country',
  site: 'site',
  zone: 'zone',
  section: 'section',
  tag: 'tag',
  milestone: 'milestone'
} as const

interface FilterState {
  allLevels: StatusFilter
  studyLevel: StatusFilter
  countryLevel: StatusFilter
  siteLevel: StatusFilter
}

interface DataState {
  allLevels: GraphData
  studyLevel: GraphData
  countryLevel: GraphData
  siteLevel: GraphData
}

interface SelectOption {
  fsiId?: string
  fsiName?: string
  id?: any
  name?: string
  value: any
  label: string
}

interface FilteredOptions {
  allLevels: Record<string, SelectOption[]>
  studyLevel: Record<string, SelectOption[]>
  countryLevel: Record<string, SelectOption[]>
  siteLevel: Record<string, SelectOption[]>
}

const Status: React.FC = () => {
  const { t } = useTranslation()
  const context = useServiceContext()
  const navigate = useNavigateWithParams()

  // State management
  const [properties, setProperties] = useState<Record<string, any>>({})
  const [data, setData] = useState<DataState>({
    allLevels: [0, 0, 0],
    studyLevel: [0, 0, 0],
    countryLevel: [0, 0, 0],
    siteLevel: [0, 0, 0]
  })

  const [filter, setFilter] = useState<FilterState>({
    allLevels: { type: 'PRODUCT' },
    studyLevel: { type: 'STUDY' },
    countryLevel: { type: 'COUNTRY' },
    siteLevel: { type: 'SITE' }
  })

  const [filteredSelectsOptions, setFilteredSelectsOptions] = useState<FilteredOptions>({
    allLevels: {},
    studyLevel: {},
    countryLevel: {},
    siteLevel: {}
  })

  const [studyDocumentsFilter, setStudyDocumentsFilter] = useState<StudyDocumentsFilterData>({
    level: 'ALL',
    completionType: 'ALL'
  })
  const [deeplink, setDeeplink] = useState(false)
  const [isInjectingFilter, setIsInjectingFilter] = useState(false)

  // User role check
  const USER_ROLE = context.service.auth.getRole()
  const Constants = (context as any).constant
  const IS_ETMF_SPECIALIST_OR_SPONSOR =
    USER_ROLE === Constants?.user?.roles?.ETMF_SPECIALIST || USER_ROLE === Constants?.user?.roles?.ETMF_SPONSOR

  // Initialize on mount
  useEffect(() => {
    initializeStatus()
  }, [])

  /**
   * Initialize status dashboard - Phase 2B Implementation
   */
  const initializeStatus = async () => {
    try {
      // 1. Load linked entities to get property IDs
      const results = await context.api.etmf.getLinkedEntities()
      const cachedData = context.service.persist.getBasic<{ version: string; filter: FilterState }>('overview_table')

      // 2. Build properties map
      const propertiesData: Record<string, any> = {
        product: { id: results?.entityLinkMap?.PRODUCT },
        trial: { id: results?.entityLinkMap?.TRIAL },
        environment: { id: results?.entityLinkMap?.ENVIRONMENT },
        country: { id: results?.entityLinkMap?.COUNTRY },
        site: { id: results?.entityLinkMap?.SITE },
        level: { id: results?.entityLinkMap?.LEVEL },
        zone: { id: results?.entityLinkMap?.ZONE },
        section: { id: results?.entityLinkMap?.SECTION },
        [Constants.etmf.property.MILESTONE.toLowerCase()]: {
          id: results?.entityLinkMap?.MILESTONE,
          usedValues: []
        },
        stage: { id: results?.entityLinkMap?.STAGE, usedValues: [] },
        rule: { id: results?.entityLinkMap?.RULE, usedValues: [] }
      }

      // 3. Load property values for milestone, stage, rule
      const propertyKeys = ['milestone', 'stage', 'rule']
      await Promise.all(
        Object.entries(propertiesData).map(async ([key, item]) => {
          if (item.id && propertyKeys.includes(key)) {
            item.usedValues = await context.api.etmf.propertyValuesSearch(item.id)
          }
        })
      )

      setProperties(propertiesData)

      // 4. Check for URL params, cached data, or reload fresh
      // TODO: Implement URL params handling (prefillFromUrlParams)
      if (cachedData && cachedData.version === '3.1') {
        if (cachedData.filter) {
          setFilter(cachedData.filter)
        }
        await reloadAllSections(true, cachedData.filter)
      } else {
        await reloadAllSections(true)
      }
    } catch (error) {
      console.error('Error initializing status:', error)
      context.service.toast.error(t('ETMF_MSG_ERROR_LOADING_STATUS'))
    }
  }

  /**
   * Handle graph legend click - convert StatusFilter to StudyDocumentsFilterData format
   */
  const onGraphLabelClick = (completionType: DocumentStatus, levelFilter: StatusFilter) => {
    // Determine the correct level based on filter.type
    // For 'allLevels' section (type='PRODUCT'), the level should be 'All'
    let level = 'ALL'
    if (levelFilter.type === 'PRODUCT') {
      level = 'All'
    } else if (levelFilter.type === 'STUDY') {
      level = 'Study'
    } else if (levelFilter.type === 'COUNTRY') {
      level = 'Country'
    } else if (levelFilter.type === 'SITE') {
      level = 'Site'
    }

    // Transform StatusFilter to StudyDocumentsFilterData format
    const studyDocumentsFilterData: StudyDocumentsFilterData = {
      level: level,
      completionType: completionType // Maps to 'NOT_STARTED', 'IN_PROGRESS', 'COMPLETED'
    }

    // Map filter fields to StudyDocumentsFilterData format using fsiId (not value)
    if (levelFilter.product) {
      studyDocumentsFilterData.PRODUCT = {
        fsiId: (levelFilter.product as any).fsiId || (levelFilter.product as any).value,
        fsiPropName: (levelFilter.product as any).fsiName || (levelFilter.product as any).label
      }
    }
    if (levelFilter.trial) {
      studyDocumentsFilterData.STUDY = {
        fsiId: (levelFilter.trial as any).fsiId || (levelFilter.trial as any).value,
        fsiPropName: (levelFilter.trial as any).fsiName || (levelFilter.trial as any).label
      }
    }
    if (levelFilter.environment) {
      studyDocumentsFilterData.ENVIRONMENT = {
        fsiId: (levelFilter.environment as any).fsiId || (levelFilter.environment as any).value,
        fsiPropName: (levelFilter.environment as any).fsiName || (levelFilter.environment as any).label
      }
    }
    if (levelFilter.country) {
      studyDocumentsFilterData.COUNTRY = {
        fsiId: (levelFilter.country as any).fsiId || (levelFilter.country as any).value,
        fsiPropName: (levelFilter.country as any).fsiName || (levelFilter.country as any).label
      }
    }
    if (levelFilter.site) {
      studyDocumentsFilterData.SITE = {
        fsiId: (levelFilter.site as any).fsiId || (levelFilter.site as any).value,
        fsiPropName: (levelFilter.site as any).fsiName || (levelFilter.site as any).label
      }
    }

    // Add metadata properties if present
    if (levelFilter.tag) {
      studyDocumentsFilterData.tagIds = [
        {
          id: (levelFilter.tag as any).value,
          label: (levelFilter.tag as any).label
        }
      ]
    }
    if (levelFilter.milestone) {
      studyDocumentsFilterData.milestone = {
        label: (levelFilter.milestone as any).label
      }
    }
    if (levelFilter.stage) {
      studyDocumentsFilterData.stage = {
        label: (levelFilter.stage as any).label
      }
    }
    if (levelFilter.rule) {
      studyDocumentsFilterData.rule = {
        label: (levelFilter.rule as any).label
      }
    }

    setStudyDocumentsFilter(studyDocumentsFilterData)
  }

  /**
   * Check if user can navigate to parent folder
   */
  const canGoToFolder = (level: keyof FilterState, type: string): boolean => {
    const targetFilter = filter[level]?.[type.toLowerCase() as keyof StatusFilter]
    return !!targetFilter
  }

  /**
   * Navigate to parent folder in file browser
   */
  const folderRedirect = (targetObject: any) => {
    if (targetObject?.value) {
      navigate(`/browser/${targetObject.value}`)
    } else {
      context.service.toast.warning(t('ETMF_MSG_ERROR_GET_CLINICAL_FOLDER'))
    }
  }

  /**
   * Navigate to parent folder in file browser
   */
  const goToFolder = (type: string, filterData: StatusFilter) => {
    let targetObject: any = null

    switch (type) {
      case Constants.etmf.masterData.type.ENVIRONMENT:
        targetObject = filterData?.environment
        break
      case Constants.etmf.masterData.type.TRIAL:
        targetObject = filterData?.trial
        break
      case Constants.etmf.masterData.type.COUNTRY:
        if ((filterData.country as any)?.value === 'all') {
          // Get first country's parent folder
          const firstCountry = filteredSelectsOptions.countryLevel.country?.[1]
          if (firstCountry?.fsiId) {
            context.api.files.getDetails(Number(firstCountry.fsiId)).then((data: any) => {
              folderRedirect({ value: data?.parent?.id })
            })
          }
          return
        }
        targetObject = filterData?.country
        break
      case Constants.etmf.masterData.type.SITE:
        if ((filterData.site as any)?.value === 'all') {
          // Get first site's parent folder
          const firstSite = filteredSelectsOptions.siteLevel.site?.[1]
          if (firstSite?.fsiId) {
            context.api.files.getDetails(Number(firstSite.fsiId)).then((data: any) => {
              folderRedirect({ value: data?.parent?.id })
            })
          }
          return
        }
        targetObject = filterData?.site
        break
    }

    if (targetObject) {
      folderRedirect(targetObject)
    }
  }

  /**
   * Handle filter change
   */
  const onFilterChange = async (type: keyof FilterState, name: string) => {
    try {
      const filterData = cloneDeep(filter)
      const updatedFilter = await reloadSection(type, name, true, false, filterData)
      await loadEtmfStatus(type, false, updatedFilter)
      storeCache(updatedFilter)
    } catch (error) {
      console.error('Filter change error:', error)
    }
  }

  /**
   * Helper: Get ETMF level constant from section key
   */
  const getEtmfLevelFromKey = (level: keyof DataState): string => {
    switch (level) {
      case 'allLevels':
        return 'ALL'
      case 'studyLevel':
        return Constants.etmf.level.STUDY
      case 'countryLevel':
        return Constants.etmf.level.COUNTRY
      case 'siteLevel':
        return Constants.etmf.level.SITE
      default:
        return 'ALL'
    }
  }

  /**
   * Helper: Get fsiId from filter based on hierarchy
   */
  const getFsiIdForLevel = (level: keyof DataState, sectionFilter: StatusFilter): string | null => {
    switch (level) {
      case 'allLevels':
      case 'studyLevel':
        if (sectionFilter?.environment) {
          return (sectionFilter.environment as any)?.value || null
        } else if (sectionFilter?.trial) {
          return (sectionFilter.trial as any)?.value || null
        } else if (sectionFilter?.product) {
          return (sectionFilter.product as any)?.value || null
        }
        return null
      case 'countryLevel':
        // Only return fsiId if country filter is explicitly set
        if (sectionFilter?.country) {
          const countryValue = sectionFilter.country as any
          // If 'All Countries' is selected, use environment fsiId
          if (countryValue?.value === 'all') {
            return (sectionFilter.environment as any)?.value || null
          }
          return countryValue?.value || null
        }
        // Don't make API call until country is selected
        return null
      case 'siteLevel':
        // Only return fsiId if site filter is explicitly set
        if (sectionFilter?.site) {
          const siteValue = sectionFilter.site as any
          // If 'All Sites' is selected, use country fsiId
          if (siteValue?.value === 'all') {
            return (sectionFilter.country as any)?.value || null
          }
          return siteValue?.value || null
        }
        // Don't make API call until site is selected
        return null
      default:
        return null
    }
  }

  /**
   * Update individual level data state
   */
  const updateLevelData = (level: keyof DataState, graphData: GraphData) => {
    setData((prev) => ({
      ...prev,
      [level]: graphData
    }))
  }

  /**
   * Load ETMF status for a specific level - Phase 2B Implementation
   */
  const loadEtmfStatus = async (
    level: keyof DataState,
    isSilent: boolean = false,
    filterData: FilterState = filter
  ): Promise<void> => {
    const sectionFilter = filterData[level]

    // Extract filter parameters
    const milestone = (sectionFilter as any)?.milestone?.label || null
    const stage = (sectionFilter as any)?.stage?.label || null
    const rule = (sectionFilter as any)?.rule?.label || null
    const tagId = (sectionFilter as any)?.tag?.value || null

    // Determine ETMF level
    const etmfLevel = getEtmfLevelFromKey(level)

    // Determine fsiId based on filter hierarchy
    const fsiId = getFsiIdForLevel(level, sectionFilter)

    if (fsiId) {
      try {
        const filterCopy = cloneDeep(filterData[level])
        const status = await context.api.etmf.getStatusByLevel(
          etmfLevel,
          fsiId,
          { milestone, rule, stage, tagIds: tagId },
          isSilent
        )

        // Only update if filter hasn't changed
        if (JSON.stringify(filterData[level]) === JSON.stringify(filterCopy)) {
          const graphData: GraphData = [
            status.placeholdersCount,
            status.documentsInCRCount,
            status.effectiveDocumentsCount
          ]
          updateLevelData(level, graphData)
        }
      } catch (error) {
        console.error(`Error loading ETMF status for ${level}:`, error)
        updateLevelData(level, [0, 0, 0])
      } finally {
        if (!(sectionFilter as any)?.tag && deeplink) {
          context.service.toast.warning(t('TAG_NOT_ASSOCIATED'))
        }
        setDeeplink(false)
      }
    } else {
      // No filter selected, reset to zeros
      updateLevelData(level, [0, 0, 0])
    }
  }

  /**
   * Download products for all levels
   */
  const downloadProducts = async (): Promise<void> => {
    try {
      const products = await context.api.etmf.getSelectionsForEtmfReports(Constants.etmf.level.PRODUCT, undefined, true)

      const productOptions = products.map((p: any) => ({
        fsiId: p.fsiId,
        fsiName: p.fsiName,
        value: p.fsiId,
        label: p.fsiName
      }))

      setFilteredSelectsOptions((prev) => ({
        ...prev,
        allLevels: { ...prev.allLevels, product: productOptions },
        studyLevel: { ...prev.studyLevel, product: productOptions },
        countryLevel: { ...prev.countryLevel, product: productOptions },
        siteLevel: { ...prev.siteLevel, product: productOptions }
      }))
    } catch (error) {
      console.error('Error downloading products:', error)
    }
  }

  /**
   * Reload section dispatcher - Implements filter cascade with auto-selection
   */
  const reloadSection = async (
    type: keyof FilterState,
    name: string | null,
    clear: boolean,
    silent: boolean,
    filterData: FilterState = filter
  ): Promise<FilterState> => {
    const sectionFilter = filterData[type]
    const etmfLevel = getEtmfLevelFromKey(type)

    // Determine what to reload based on the changed filter
    switch (name) {
      case 'product':
        await loadTrialsForSection(type, filterData)
        break
      case 'trial':
        await loadEnvironmentsForSection(type, filterData)
        break
      case 'environment':
        // Load countries only for countryLevel and siteLevel
        if (type === 'countryLevel' || type === 'siteLevel') {
          await loadCountriesForSection(type, filterData)
        }
        break
      case 'country':
        // Load sites only for siteLevel
        if (type === 'siteLevel') {
          await loadSitesForSection(type, filterData)
        }
        break
    }

    return filterData
  }

  /**
   * Load trials for a section and auto-select if only one
   */
  const loadTrialsForSection = async (type: keyof FilterState, filterData: FilterState): Promise<void> => {
    const sectionFilter = filterData[type]
    const productId = (sectionFilter?.product as any)?.value

    if (!productId) return

    try {
      const trials = await context.api.etmf.getSelectionsForEtmfReports(Constants.etmf.level.STUDY, productId, true)

      const trialOptions = trials.map((t: any) => ({
        fsiId: t.fsiId,
        fsiName: t.fsiName,
        value: t.fsiId,
        label: t.fsiName || ''
      }))

      // Update options
      setFilteredSelectsOptions((prev) => ({
        ...prev,
        [type]: { ...prev[type], trial: trialOptions }
      }))

      // Auto-select if only one trial
      if (trialOptions.length === 1) {
        filterData[type] = {
          ...sectionFilter,
          trial: trialOptions[0],
          environment: null,
          country: null,
          site: null,
          tag: null
        }
        setFilter(filterData)

        // Auto-load environments
        await loadEnvironmentsForSection(type, filterData)

        // Load graph data for the auto-selected trial
        await loadEtmfStatus(type, true, filterData)
      }
    } catch (error) {
      console.error('Error loading trials:', error)
    }
  }

  /**
   * Load environments for a section and auto-select if only one
   */
  const loadEnvironmentsForSection = async (type: keyof FilterState, filterData: FilterState): Promise<void> => {
    const sectionFilter = filterData[type]
    const trialId = (sectionFilter?.trial as any)?.value

    if (!trialId) return

    try {
      const environments = await context.api.etmf.getSelectionsForEtmfReports(
        Constants.etmf.level.ENVIRONMENT,
        trialId,
        true
      )

      const environmentOptions = environments.map((e: any) => ({
        fsiId: e.fsiId,
        fsiName: e.fsiName,
        value: e.fsiId,
        label: e.fsiName || ''
      }))

      // Update options
      setFilteredSelectsOptions((prev) => ({
        ...prev,
        [type]: { ...prev[type], environment: environmentOptions }
      }))

      // Auto-select if only one environment
      if (environmentOptions.length === 1) {
        filterData[type] = {
          ...sectionFilter,
          environment: environmentOptions[0],
          country: null,
          site: null,
          tag: null
        }
        setFilter(filterData)

        // Auto-load countries for countryLevel and siteLevel
        if (type === 'countryLevel' || type === 'siteLevel') {
          await loadCountriesForSection(type, filterData)
        }

        // Load graph data for the auto-selected environment
        await loadEtmfStatus(type, true, filterData)
      }
    } catch (error) {
      console.error('Error loading environments:', error)
    }
  }

  /**
   * Load countries for a section and auto-select if only one
   */
  const loadCountriesForSection = async (type: keyof FilterState, filterData: FilterState): Promise<void> => {
    const sectionFilter = filterData[type]
    const environmentId = (sectionFilter?.environment as any)?.value

    if (!environmentId) return

    try {
      const countries = await context.api.etmf.getSelectionsForEtmfReports(
        Constants.etmf.level.COUNTRY,
        environmentId,
        true
      )

      const countryOptions = countries.map((c: any) => ({
        fsiId: c.fsiId,
        fsiName: c.fsiName,
        value: c.fsiId,
        label: c.fsiName || ''
      }))

      // Update options
      setFilteredSelectsOptions((prev) => ({
        ...prev,
        [type]: { ...prev[type], country: countryOptions }
      }))

      // Auto-select if only one country
      if (countryOptions.length === 1) {
        filterData[type] = {
          ...sectionFilter,
          country: countryOptions[0],
          site: null,
          tag: null
        }
        setFilter(filterData)

        // Auto-load sites for siteLevel only
        if (type === 'siteLevel') {
          await loadSitesForSection(type, filterData)
        }

        // Load graph data for the auto-selected country
        await loadEtmfStatus(type, true, filterData)
      }
    } catch (error) {
      console.error('Error loading countries:', error)
    }
  }

  /**
   * Load sites for a section and auto-select if only one
   */
  const loadSitesForSection = async (type: keyof FilterState, filterData: FilterState): Promise<void> => {
    const sectionFilter = filterData[type]
    const countryId = (sectionFilter?.country as any)?.value

    // Skip if no country or if 'All Countries' is selected
    if (!countryId || countryId === 'all') return

    try {
      const sites = await context.api.etmf.getSelectionsForEtmfReports(Constants.etmf.level.SITE, countryId, true)

      const siteOptions = sites.map((s: any) => ({
        fsiId: s.fsiId,
        fsiName: s.fsiName,
        value: s.fsiId,
        label: s.fsiName || ''
      }))

      // Update options
      setFilteredSelectsOptions((prev) => ({
        ...prev,
        [type]: { ...prev[type], site: siteOptions }
      }))

      // Auto-select if only one site
      if (siteOptions.length === 1) {
        filterData[type] = {
          ...sectionFilter,
          site: siteOptions[0],
          tag: null
        }
        setFilter(filterData)

        // Load graph data for the auto-selected site
        await loadEtmfStatus(type, true, filterData)
      }
    } catch (error) {
      console.error('Error loading sites:', error)
    }
  }

  /**
   * Reload all sections - Phase 2B Implementation
   */
  const reloadAllSections = async (initialLoad: boolean, filterData: FilterState = filter): Promise<void> => {
    try {
      // 1. Download products first
      await downloadProducts()

      // 2. For each section, reload cascading dropdowns based on cached filter values
      const sections: Array<keyof FilterState> = ['allLevels', 'studyLevel', 'countryLevel', 'siteLevel']

      for (const section of sections) {
        const sectionFilter = filterData[section]

        // Reload trials if product is set
        if (sectionFilter?.product) {
          await loadTrialsForSection(section, filterData)
        }

        // Reload environments if trial is set
        if (sectionFilter?.trial) {
          await loadEnvironmentsForSection(section, filterData)
        }

        // Reload sites if country is set (for siteLevel only)
        if (section === 'siteLevel' && sectionFilter?.country) {
          await loadSitesForSection(section, filterData)
        }
      }

      // 3. Load ETMF status for all sections
      const isSilent = !initialLoad
      await Promise.all([
        loadEtmfStatus('allLevels', isSilent, filterData),
        loadEtmfStatus('studyLevel', isSilent, filterData),
        loadEtmfStatus('countryLevel', isSilent, filterData),
        loadEtmfStatus('siteLevel', isSilent, filterData)
      ])

      // 4. Store cache
      storeCache(filterData)
    } catch (error) {
      console.error('Error reloading all sections:', error)
    }
  }

  /**
   * Store filter state in cache
   */
  const storeCache = (filterData: FilterState): void => {
    const cacheData = {
      version: '3.1',
      filter: filterData
    }
    context.service.persist.setBasic('overview_table', cacheData)
  }

  // TODO Phase 2C: Add "Go to folder" tooltip when implementing navigation

  return (
    <div id="etmfStatusPage-content">
      <div className="row d-flex align-items-stretch">
        {/* All Levels Section */}
        <div className="col-xs-3" id="section-allLevels">
          <div className="graph-wrapper">
            <h3>{t('ETMF_GRAPHS_TITLE_ALL_LEVELS')}</h3>
            <StatusGraph data={data.allLevels} filter={filter.allLevels} onStatsClick={onGraphLabelClick} />

            {/* Product Filter */}
            <div className="form-group w100p mb7">
              <ControlLabel>{t('EMTF_MD_PRODUCT(S)')}</ControlLabel>
              <Select
                value={filter.allLevels.product?.value ?? null}
                name="allLevelsProduct"
                placeholder={t('SELECT_PRODUCT')}
                onChange={(option: any) => {
                  const filterData = { ...filter }
                  filterData.allLevels.product = option
                  filterData.allLevels.trial = null
                  filterData.allLevels.environment = null
                  filterData.allLevels.tag = null
                  filterData.allLevels.milestone = null
                  filterData.allLevels.stage = null
                  filterData.allLevels.rule = null
                  setFilter(filterData)
                  onFilterChange('allLevels', 'product')
                }}
                options={
                  filteredSelectsOptions.allLevels.product?.map((p) => ({
                    value: p.fsiId,
                    label: p.fsiName || '',
                    fsiId: p.fsiId,
                    fsiName: p.fsiName
                  })) ?? []
                }
              />
            </div>

            {/* Trial Filter */}
            <div
              className={`form-group w100p mb7 ${!filter.allLevels.product || !filteredSelectsOptions.allLevels.trial?.length ? 'disabled' : ''}`}
            >
              <ControlLabel>{t('EMTF_MD_TRIAL')}</ControlLabel>
              <div className="select-with-btn-wrapper etmf-overview-new">
                <Select
                  value={filter.allLevels.trial?.value ?? null}
                  name="allLevelsTrial"
                  placeholder={t('ETMF_LABEL_SELECT_STUDY')}
                  onChange={(option: any) => {
                    const filterData = { ...filter }
                    filterData.allLevels.trial = option
                    filterData.allLevels.environment = null
                    filterData.allLevels.tag = null
                    filterData.allLevels.milestone = null
                    filterData.allLevels.stage = null
                    filterData.allLevels.rule = null
                    setFilter(filterData)
                    onFilterChange('allLevels', 'trial')
                  }}
                  options={
                    filteredSelectsOptions.allLevels.trial?.map((t) => ({
                      value: t.fsiId,
                      label: t.fsiName || '',
                      fsiId: t.fsiId,
                      fsiName: t.fsiName
                    })) ?? []
                  }
                  isDisabled={!filter.allLevels.product || !filteredSelectsOptions.allLevels.trial?.length}
                />
                <button
                  type="button"
                  className={`goto-folder react-version-cta ${
                    !filter.allLevels.product || !filter.allLevels.trial || IS_ETMF_SPECIALIST_OR_SPONSOR
                      ? 'disabled'
                      : ''
                  }`}
                  onClick={() => goToFolder(Constants.etmf.masterData.type.TRIAL, filter.allLevels)}
                  disabled={!filter.allLevels.product || !filter.allLevels.trial || IS_ETMF_SPECIALIST_OR_SPONSOR}
                  title={t('ETMF_TOOLTIP_GO_TO_PARENT_FOLDER').replace(/<[^>]*>/g, '')}
                />
              </div>
            </div>

            {/* Environment Filter */}
            <div
              className={`form-group w100p mb7 ${!filter.allLevels.trial || !filteredSelectsOptions.allLevels.environment?.length ? 'disabled' : ''} ${!properties.milestone?.id ? 'mb0' : 'mb7'}`}
            >
              <ControlLabel>{t('EMTF_MD_ENVIRONMENT')}</ControlLabel>
              <div className="select-with-btn-wrapper etmf-overview-new">
                <Select
                  value={filter.allLevels.environment?.value ?? null}
                  name="allLevelsEnvironment"
                  placeholder={t('SELECT_ENVIRONMENT')}
                  onChange={(option: any) => {
                    const filterData = { ...filter }
                    filterData.allLevels.environment = option
                    filterData.allLevels.tag = null
                    filterData.allLevels.milestone = null
                    filterData.allLevels.stage = null
                    filterData.allLevels.rule = null
                    setFilter(filterData)
                    onFilterChange('allLevels', 'environment')
                  }}
                  options={
                    filteredSelectsOptions.allLevels.environment?.map((e) => ({
                      value: e.fsiId,
                      label: e.fsiName || '',
                      fsiId: e.fsiId,
                      fsiName: e.fsiName
                    })) ?? []
                  }
                  isDisabled={!filter.allLevels.trial || !filteredSelectsOptions.allLevels.environment?.length}
                />
                <button
                  type="button"
                  className={`goto-folder react-version-cta ${
                    !canGoToFolder('allLevels', Constants.etmf.masterData.type.ENVIRONMENT) ? 'disabled' : ''
                  }`}
                  onClick={() => goToFolder(Constants.etmf.masterData.type.ENVIRONMENT, filter.allLevels)}
                  disabled={!canGoToFolder('allLevels', Constants.etmf.masterData.type.ENVIRONMENT)}
                  title={t('ETMF_TOOLTIP_GO_TO_PARENT_FOLDER').replace(/<[^>]*>/g, '')}
                />
              </div>
            </div>

            {/* Tag Filter */}
            <div
              className={`form-group w100p mb7 ${!filter.allLevels.environment || !filteredSelectsOptions.allLevels.tag?.length ? 'disabled' : ''}`}
            >
              <ControlLabel>{t('TAG_FILTER')}</ControlLabel>
              <Select
                value={filter.allLevels.tag?.value ?? null}
                name="allLevelsTag"
                placeholder={t('SELECT_TAG')}
                onChange={(option: any) => {
                  const filterData = { ...filter }
                  filterData.allLevels.tag = option
                  setFilter(filterData)
                  onFilterChange('allLevels', 'tag')
                }}
                options={
                  filteredSelectsOptions.allLevels.tag?.map((tag) => ({ value: tag.id, label: tag.name || '' })) ?? []
                }
                isDisabled={!filter.allLevels.environment || !filteredSelectsOptions.allLevels.tag?.length}
              />
            </div>

            {/* Milestone Filter (conditional) */}
            {!!properties.milestone?.id && (
              <div
                className={`form-group w100p mb7 ${!filter.allLevels.trial || !properties.milestone?.usedValues?.length ? 'disabled' : ''}`}
              >
                <ControlLabel>{t('ETMF_OVERVIEW_MILESTONE')}</ControlLabel>
                <Select
                  value={filter.allLevels.milestone?.value ?? null}
                  name="allLevelsMilestone"
                  placeholder={t('ETMF_LABEL_SELECT_MILESTONE')}
                  onChange={(option: any) => {
                    const filterData = { ...filter }
                    filterData.allLevels.milestone = option
                    setFilter(filterData)
                    onFilterChange('allLevels', 'milestone')
                  }}
                  options={
                    properties.milestone?.usedValues?.map((item: any, index: number) => ({
                      value: index + 1,
                      label: item
                    })) ?? []
                  }
                  isDisabled={!filter.allLevels.trial || !properties.milestone?.usedValues?.length}
                />
              </div>
            )}

            {/* Stage Filter (conditional) */}
            {!!properties.stage?.id && (
              <div
                className={`form-group w100p mb7 ${!filter.allLevels.trial || !properties.stage?.usedValues?.length ? 'disabled' : ''}`}
              >
                <ControlLabel>{t('ETMF_LABEL_SELECT_STAGE')}</ControlLabel>
                <Select
                  value={filter.allLevels.stage?.value ?? null}
                  name="allLevelsStage"
                  placeholder={t('ETMF_LABEL_SELECT_STAGE')}
                  onChange={(option: any) => {
                    const filterData = { ...filter }
                    filterData.allLevels.stage = option
                    setFilter(filterData)
                    onFilterChange('allLevels', 'stage')
                  }}
                  options={
                    properties.stage?.usedValues?.map((item: any, index: number) => ({
                      value: index + 1,
                      label: item
                    })) ?? []
                  }
                  isDisabled={!filter.allLevels.trial || !properties.stage?.usedValues?.length}
                />
              </div>
            )}

            {/* Rule Filter (conditional) */}
            {!!properties.rule?.id && (
              <div
                className={`form-group w100p mb7 ${!filter.allLevels.trial || !properties.rule?.usedValues?.length ? 'disabled' : ''}`}
              >
                <ControlLabel>{t('ETMF_LABEL_SELECT_RULE')}</ControlLabel>
                <Select
                  value={filter.allLevels.rule?.value ?? null}
                  name="allLevelsRule"
                  placeholder={t('ETMF_LABEL_SELECT_RULE')}
                  onChange={(option: any) => {
                    const filterData = { ...filter }
                    filterData.allLevels.rule = option
                    setFilter(filterData)
                    onFilterChange('allLevels', 'rule')
                  }}
                  options={
                    properties.rule?.usedValues?.map((item: any, index: number) => ({
                      value: index + 1,
                      label: item
                    })) ?? []
                  }
                  isDisabled={!filter.allLevels.trial || !properties.rule?.usedValues?.length}
                />
              </div>
            )}
          </div>
        </div>

        {/* Study Level Section */}
        <div className="col-xs-3" id="section-studyLevel">
          <div className="graph-wrapper">
            <h3>{t('ETMF_GRAPHS_TITLE_STUDY_LEVEL')}</h3>
            <StatusGraph data={data.studyLevel} filter={filter.studyLevel} onStatsClick={onGraphLabelClick} />

            {/* Product Filter */}
            <div className="form-group w100p mb7">
              <ControlLabel className="with-asterisk">{t('EMTF_MD_PRODUCT(S)')}</ControlLabel>
              <Select
                value={filter.studyLevel.product?.value ?? null}
                name="studyLevelProduct"
                placeholder={t('SELECT_PRODUCT')}
                onChange={(option: any) => {
                  const filterData = { ...filter }
                  filterData.studyLevel.product = option
                  filterData.studyLevel.trial = null
                  filterData.studyLevel.environment = null
                  filterData.studyLevel.tag = null
                  filterData.studyLevel.milestone = null
                  filterData.studyLevel.stage = null
                  filterData.studyLevel.rule = null
                  setFilter(filterData)
                  onFilterChange('studyLevel', 'product')
                }}
                options={
                  filteredSelectsOptions.studyLevel.product?.map((p) => ({
                    value: p.fsiId,
                    label: p.fsiName || '',
                    fsiId: p.fsiId,
                    fsiName: p.fsiName
                  })) ?? []
                }
              />
            </div>

            {/* Trial Filter */}
            <div
              className={`form-group w100p mb7 ${!filter.studyLevel.product || !filteredSelectsOptions.studyLevel.trial?.length ? 'disabled' : ''}`}
            >
              <ControlLabel className="with-asterisk">{t('EMTF_MD_TRIAL')}</ControlLabel>
              <div className="select-with-btn-wrapper etmf-overview-new">
                <Select
                  value={filter.studyLevel.trial?.value ?? null}
                  name="studyLevelTrial"
                  placeholder={t('ETMF_LABEL_SELECT_STUDY')}
                  onChange={(option: any) => {
                    const filterData = { ...filter }
                    filterData.studyLevel.trial = option
                    filterData.studyLevel.environment = null
                    filterData.studyLevel.tag = null
                    filterData.studyLevel.milestone = null
                    filterData.studyLevel.stage = null
                    filterData.studyLevel.rule = null
                    setFilter(filterData)
                    onFilterChange('studyLevel', 'trial')
                  }}
                  options={
                    filteredSelectsOptions.studyLevel.trial?.map((t) => ({
                      value: t.fsiId,
                      label: t.fsiName || '',
                      fsiId: t.fsiId,
                      fsiName: t.fsiName
                    })) ?? []
                  }
                  isDisabled={!filter.studyLevel.product || !filteredSelectsOptions.studyLevel.trial?.length}
                />
                <button
                  type="button"
                  className={`goto-folder react-version-cta ${
                    !filter.studyLevel.product || !filter.studyLevel.trial || IS_ETMF_SPECIALIST_OR_SPONSOR
                      ? 'disabled'
                      : ''
                  }`}
                  onClick={() => goToFolder(Constants.etmf.masterData.type.TRIAL, filter.studyLevel)}
                  disabled={!filter.studyLevel.product || !filter.studyLevel.trial || IS_ETMF_SPECIALIST_OR_SPONSOR}
                  title={t('ETMF_TOOLTIP_GO_TO_PARENT_FOLDER').replace(/<[^>]*>/g, '')}
                />
              </div>
            </div>

            {/* Environment Filter */}
            <div
              className={`form-group w100p mb7 ${!filter.studyLevel.trial || !filteredSelectsOptions.studyLevel.environment?.length ? 'disabled' : ''} ${!properties.milestone?.id ? 'mb0' : 'mb7'}`}
            >
              <ControlLabel className="with-asterisk">{t('EMTF_MD_ENVIRONMENT')}</ControlLabel>
              <div className="select-with-btn-wrapper etmf-overview-new">
                <Select
                  value={filter.studyLevel.environment?.value ?? null}
                  name="studyLevelEnvironment"
                  placeholder={t('SELECT_ENVIRONMENT')}
                  onChange={(option: any) => {
                    const filterData = { ...filter }
                    filterData.studyLevel.environment = option
                    filterData.studyLevel.tag = null
                    filterData.studyLevel.milestone = null
                    filterData.studyLevel.stage = null
                    filterData.studyLevel.rule = null
                    setFilter(filterData)
                    onFilterChange('studyLevel', 'environment')
                  }}
                  options={
                    filteredSelectsOptions.studyLevel.environment?.map((e) => ({
                      value: e.fsiId,
                      label: e.fsiName || '',
                      fsiId: e.fsiId,
                      fsiName: e.fsiName
                    })) ?? []
                  }
                  isDisabled={!filter.studyLevel.trial || !filteredSelectsOptions.studyLevel.environment?.length}
                />
                <button
                  type="button"
                  className={`goto-folder react-version-cta ${
                    !canGoToFolder('studyLevel', Constants.etmf.masterData.type.ENVIRONMENT) ? 'disabled' : ''
                  }`}
                  onClick={() => goToFolder(Constants.etmf.masterData.type.ENVIRONMENT, filter.studyLevel)}
                  disabled={!canGoToFolder('studyLevel', Constants.etmf.masterData.type.ENVIRONMENT)}
                  title={t('ETMF_TOOLTIP_GO_TO_PARENT_FOLDER').replace(/<[^>]*>/g, '')}
                />
              </div>
            </div>

            {/* Tag Filter */}
            <div
              className={`form-group w100p mb7 ${!filter.studyLevel.environment || !filteredSelectsOptions.studyLevel.tag?.length ? 'disabled' : ''}`}
            >
              <ControlLabel>{t('TAG_FILTER')}</ControlLabel>
              <Select
                value={filter.studyLevel.tag?.value ?? null}
                name="studyLevelTag"
                placeholder={t('SELECT_TAG')}
                onChange={(option: any) => {
                  const filterData = { ...filter }
                  filterData.studyLevel.tag = option
                  setFilter(filterData)
                  onFilterChange('studyLevel', 'tag')
                }}
                options={
                  filteredSelectsOptions.studyLevel.tag?.map((tag) => ({ value: tag.id, label: tag.name || '' })) ?? []
                }
                isDisabled={!filter.studyLevel.environment || !filteredSelectsOptions.studyLevel.tag?.length}
              />
            </div>

            {/* Milestone Filter (conditional) */}
            {!!properties.milestone?.id && (
              <div
                className={`form-group w100p mb7 ${!filter.studyLevel.trial || !properties.milestone?.usedValues?.length ? 'disabled' : ''}`}
              >
                <ControlLabel>{t('ETMF_OVERVIEW_MILESTONE')}</ControlLabel>
                <Select
                  value={filter.studyLevel.milestone?.value ?? null}
                  name="studyLevelMilestone"
                  placeholder={t('ETMF_LABEL_SELECT_MILESTONE')}
                  onChange={(option: any) => {
                    const filterData = { ...filter }
                    filterData.studyLevel.milestone = option
                    setFilter(filterData)
                    onFilterChange('studyLevel', 'milestone')
                  }}
                  options={
                    properties.milestone?.usedValues?.map((item: any, index: number) => ({
                      value: index + 1,
                      label: item
                    })) ?? []
                  }
                  isDisabled={!filter.studyLevel.trial || !properties.milestone?.usedValues?.length}
                />
              </div>
            )}

            {/* Stage Filter (conditional) */}
            {!!properties.stage?.id && (
              <div
                className={`form-group w100p mb7 ${!filter.studyLevel.trial || !properties.stage?.usedValues?.length ? 'disabled' : ''}`}
              >
                <ControlLabel>{t('ETMF_LABEL_SELECT_STAGE')}</ControlLabel>
                <Select
                  value={filter.studyLevel.stage?.value ?? null}
                  name="studyLevelStage"
                  placeholder={t('ETMF_LABEL_SELECT_STAGE')}
                  onChange={(option: any) => {
                    const filterData = { ...filter }
                    filterData.studyLevel.stage = option
                    setFilter(filterData)
                    onFilterChange('studyLevel', 'stage')
                  }}
                  options={
                    properties.stage?.usedValues?.map((item: any, index: number) => ({
                      value: index + 1,
                      label: item
                    })) ?? []
                  }
                  isDisabled={!filter.studyLevel.trial || !properties.stage?.usedValues?.length}
                />
              </div>
            )}

            {/* Rule Filter (conditional) */}
            {!!properties.rule?.id && (
              <div
                className={`form-group w100p mb7 ${!filter.studyLevel.trial || !properties.rule?.usedValues?.length ? 'disabled' : ''}`}
              >
                <ControlLabel>{t('ETMF_LABEL_SELECT_RULE')}</ControlLabel>
                <Select
                  value={filter.studyLevel.rule?.value ?? null}
                  name="studyLevelRule"
                  placeholder={t('ETMF_LABEL_SELECT_RULE')}
                  onChange={(option: any) => {
                    const filterData = { ...filter }
                    filterData.studyLevel.rule = option
                    setFilter(filterData)
                    onFilterChange('studyLevel', 'rule')
                  }}
                  options={
                    properties.rule?.usedValues?.map((item: any, index: number) => ({
                      value: index + 1,
                      label: item
                    })) ?? []
                  }
                  isDisabled={!filter.studyLevel.trial || !properties.rule?.usedValues?.length}
                />
              </div>
            )}
          </div>
        </div>

        {/* Country Level Section */}
        <div className="col-xs-3" id="section-countryLevel">
          <div className="graph-wrapper">
            <h3>{t('ETMF_GRAPHS_TITLE_COUNTRY_LEVEL')}</h3>
            <StatusGraph data={data.countryLevel} filter={filter.countryLevel} onStatsClick={onGraphLabelClick} />

            {/* Product Filter */}
            <div className="form-group w100p mb7">
              <ControlLabel className="with-asterisk">{t('EMTF_MD_PRODUCT(S)')}</ControlLabel>
              <Select
                value={filter.countryLevel.product?.value ?? null}
                name="countryLevelProduct"
                placeholder={t('SELECT_PRODUCT')}
                onChange={(option: any) => {
                  const filterData = { ...filter }
                  filterData.countryLevel.product = option
                  filterData.countryLevel.trial = null
                  filterData.countryLevel.environment = null
                  filterData.countryLevel.country = null
                  filterData.countryLevel.tag = null
                  filterData.countryLevel.milestone = null
                  filterData.countryLevel.stage = null
                  filterData.countryLevel.rule = null
                  setFilter(filterData)
                  onFilterChange('countryLevel', 'product')
                }}
                options={
                  filteredSelectsOptions.countryLevel.product?.map((p) => ({
                    value: p.fsiId,
                    label: p.fsiName || '',
                    fsiId: p.fsiId,
                    fsiName: p.fsiName
                  })) ?? []
                }
              />
            </div>

            {/* Trial Filter */}
            <div
              className={`form-group w100p mb7 ${!filter.countryLevel.product || !filteredSelectsOptions.countryLevel.trial?.length ? 'disabled' : ''}`}
            >
              <ControlLabel className="with-asterisk">{t('EMTF_MD_TRIAL')}</ControlLabel>
              <Select
                value={filter.countryLevel.trial?.value ?? null}
                name="countryLevelTrial"
                placeholder={t('ETMF_LABEL_SELECT_STUDY')}
                onChange={(option: any) => {
                  const filterData = { ...filter }
                  filterData.countryLevel.trial = option
                  filterData.countryLevel.environment = null
                  filterData.countryLevel.country = null
                  filterData.countryLevel.tag = null
                  filterData.countryLevel.milestone = null
                  filterData.countryLevel.stage = null
                  filterData.countryLevel.rule = null
                  setFilter(filterData)
                  onFilterChange('countryLevel', 'trial')
                }}
                options={
                  filteredSelectsOptions.countryLevel.trial?.map((t) => ({
                    value: t.fsiId,
                    label: t.fsiName || '',
                    fsiId: t.fsiId,
                    fsiName: t.fsiName
                  })) ?? []
                }
                isDisabled={!filter.countryLevel.product || !filteredSelectsOptions.countryLevel.trial?.length}
              />
            </div>

            {/* Environment Filter */}
            <div
              className={`form-group w100p mb7 ${!filter.countryLevel.trial || !filteredSelectsOptions.countryLevel.environment?.length ? 'disabled' : ''} ${!properties.milestone?.id ? 'mb0' : 'mb7'}`}
            >
              <ControlLabel className="with-asterisk">{t('EMTF_MD_ENVIRONMENT')}</ControlLabel>
              <Select
                value={filter.countryLevel.environment?.value ?? null}
                name="countryLevelEnvironment"
                placeholder={t('SELECT_ENVIRONMENT')}
                onChange={(option: any) => {
                  const filterData = { ...filter }
                  filterData.countryLevel.environment = option
                  filterData.countryLevel.country = null
                  filterData.countryLevel.tag = null
                  filterData.countryLevel.milestone = null
                  filterData.countryLevel.stage = null
                  filterData.countryLevel.rule = null
                  setFilter(filterData)
                  onFilterChange('countryLevel', 'environment')
                }}
                options={
                  filteredSelectsOptions.countryLevel.environment?.map((e) => ({
                    value: e.fsiId,
                    label: e.fsiName || '',
                    fsiId: e.fsiId,
                    fsiName: e.fsiName
                  })) ?? []
                }
                isDisabled={!filter.countryLevel.trial || !filteredSelectsOptions.countryLevel.environment?.length}
              />
            </div>

            {/* Country Filter */}
            <div
              className={`form-group w100p mb7 ${!filter.countryLevel.environment || !filteredSelectsOptions.countryLevel.country?.length ? 'disabled' : ''} ${!properties.milestone?.id ? 'mb0' : 'mb7'}`}
            >
              <ControlLabel className="with-asterisk">{t('EMTF_MD_COUNTRY')}</ControlLabel>
              <div className="select-with-btn-wrapper etmf-overview-new">
                <Select
                  value={filter.countryLevel.country?.value ?? null}
                  name="countryLevelCountry"
                  placeholder={t('ETMF_LABEL_SELECT_COUNTRY')}
                  onChange={(option: any) => {
                    const filterData = { ...filter }
                    filterData.countryLevel.country = option
                    filterData.countryLevel.tag = null
                    filterData.countryLevel.milestone = null
                    filterData.countryLevel.stage = null
                    filterData.countryLevel.rule = null
                    setFilter(filterData)
                    onFilterChange('countryLevel', 'country')
                  }}
                  options={[
                    ...(filteredSelectsOptions.countryLevel.country?.length > 1
                      ? [
                          {
                            value: 'all',
                            label: t('ALL_COUNTRIES') || 'All countries',
                            fsiId: 'all',
                            fsiName: t('ALL_COUNTRIES') || 'All countries'
                          }
                        ]
                      : []),
                    ...(filteredSelectsOptions.countryLevel.country?.map((c) => ({
                      value: c.fsiId,
                      label: c.fsiName || '',
                      fsiId: c.fsiId,
                      fsiName: c.fsiName
                    })) ?? [])
                  ]}
                  isDisabled={!filter.countryLevel.environment || !filteredSelectsOptions.countryLevel.country?.length}
                />
                <button
                  type="button"
                  className={`goto-folder react-version-cta ${
                    !canGoToFolder('countryLevel', Constants.etmf.masterData.type.COUNTRY) ? 'disabled' : ''
                  }`}
                  onClick={() => goToFolder(Constants.etmf.masterData.type.COUNTRY, filter.countryLevel)}
                  disabled={!canGoToFolder('countryLevel', Constants.etmf.masterData.type.COUNTRY)}
                  title={t('ETMF_TOOLTIP_GO_TO_PARENT_FOLDER').replace(/<[^>]*>/g, '')}
                />
              </div>
            </div>

            {/* Tag Filter */}
            <div
              className={`form-group w100p mb7 ${!filter.countryLevel.country || !filteredSelectsOptions.countryLevel.tag?.length ? 'disabled' : ''}`}
            >
              <ControlLabel>{t('TAG_FILTER')}</ControlLabel>
              <Select
                value={filter.countryLevel.tag?.value ?? null}
                name="countryLevelTag"
                placeholder={t('SELECT_TAG')}
                onChange={(option: any) => {
                  const filterData = { ...filter }
                  filterData.countryLevel.tag = option
                  setFilter(filterData)
                  onFilterChange('countryLevel', 'tag')
                }}
                options={
                  filteredSelectsOptions.countryLevel.tag?.map((tag) => ({ value: tag.id, label: tag.name || '' })) ??
                  []
                }
                isDisabled={!filter.countryLevel.country || !filteredSelectsOptions.countryLevel.tag?.length}
              />
            </div>

            {/* Milestone Filter (conditional) */}
            {!!properties.milestone?.id && (
              <div
                className={`form-group w100p mb7 ${!filter.countryLevel.trial || !properties.milestone?.usedValues?.length ? 'disabled' : ''}`}
              >
                <ControlLabel>{t('ETMF_OVERVIEW_MILESTONE')}</ControlLabel>
                <Select
                  value={filter.countryLevel.milestone?.value ?? null}
                  name="countryLevelMilestone"
                  placeholder={t('ETMF_LABEL_SELECT_MILESTONE')}
                  onChange={(option: any) => {
                    const filterData = { ...filter }
                    filterData.countryLevel.milestone = option
                    setFilter(filterData)
                    onFilterChange('countryLevel', 'milestone')
                  }}
                  options={
                    properties.milestone?.usedValues?.map((item: any, index: number) => ({
                      value: index + 1,
                      label: item
                    })) ?? []
                  }
                  isDisabled={!filter.countryLevel.trial || !properties.milestone?.usedValues?.length}
                />
              </div>
            )}

            {/* Stage Filter (conditional) */}
            {!!properties.stage?.id && (
              <div
                className={`form-group w100p mb7 ${!filter.countryLevel.trial || !properties.stage?.usedValues?.length ? 'disabled' : ''}`}
              >
                <ControlLabel>{t('ETMF_LABEL_SELECT_STAGE')}</ControlLabel>
                <Select
                  value={filter.countryLevel.stage?.value ?? null}
                  name="countryLevelStage"
                  placeholder={t('ETMF_LABEL_SELECT_STAGE')}
                  onChange={(option: any) => {
                    const filterData = { ...filter }
                    filterData.countryLevel.stage = option
                    setFilter(filterData)
                    onFilterChange('countryLevel', 'stage')
                  }}
                  options={
                    properties.stage?.usedValues?.map((item: any, index: number) => ({
                      value: index + 1,
                      label: item
                    })) ?? []
                  }
                  isDisabled={!filter.countryLevel.trial || !properties.stage?.usedValues?.length}
                />
              </div>
            )}

            {/* Rule Filter (conditional) */}
            {!!properties.rule?.id && (
              <div
                className={`form-group w100p mb7 ${!filter.countryLevel.trial || !properties.rule?.usedValues?.length ? 'disabled' : ''}`}
              >
                <ControlLabel>{t('ETMF_LABEL_SELECT_RULE')}</ControlLabel>
                <Select
                  value={filter.countryLevel.rule?.value ?? null}
                  name="countryLevelRule"
                  placeholder={t('ETMF_LABEL_SELECT_RULE')}
                  onChange={(option: any) => {
                    const filterData = { ...filter }
                    filterData.countryLevel.rule = option
                    setFilter(filterData)
                    onFilterChange('countryLevel', 'rule')
                  }}
                  options={
                    properties.rule?.usedValues?.map((item: any, index: number) => ({
                      value: index + 1,
                      label: item
                    })) ?? []
                  }
                  isDisabled={!filter.countryLevel.trial || !properties.rule?.usedValues?.length}
                />
              </div>
            )}
          </div>
        </div>

        {/* Site Level Section */}
        <div className="col-xs-3" id="section-siteLevel">
          <div className="graph-wrapper">
            <h3>{t('ETMF_GRAPHS_TITLE_SITE_LEVEL')}</h3>
            <StatusGraph data={data.siteLevel} filter={filter.siteLevel} onStatsClick={onGraphLabelClick} />

            {/* Product Filter */}
            <div className="form-group w100p mb7">
              <ControlLabel className="with-asterisk">{t('EMTF_MD_PRODUCT(S)')}</ControlLabel>
              <Select
                value={filter.siteLevel.product?.value ?? null}
                name="siteLevelProduct"
                placeholder={t('SELECT_PRODUCT')}
                onChange={(option: any) => {
                  const filterData = { ...filter }
                  filterData.siteLevel.product = option
                  filterData.siteLevel.trial = null
                  filterData.siteLevel.environment = null
                  filterData.siteLevel.country = null
                  filterData.siteLevel.site = null
                  filterData.siteLevel.tag = null
                  filterData.siteLevel.milestone = null
                  filterData.siteLevel.stage = null
                  filterData.siteLevel.rule = null
                  setFilter(filterData)
                  onFilterChange('siteLevel', 'product')
                }}
                options={
                  filteredSelectsOptions.siteLevel.product?.map((p) => ({
                    value: p.fsiId,
                    label: p.fsiName || '',
                    fsiId: p.fsiId,
                    fsiName: p.fsiName
                  })) ?? []
                }
              />
            </div>

            {/* Trial Filter */}
            <div
              className={`form-group w100p mb7 ${!filter.siteLevel.product || !filteredSelectsOptions.siteLevel.trial?.length ? 'disabled' : ''}`}
            >
              <ControlLabel className="with-asterisk">{t('EMTF_MD_TRIAL')}</ControlLabel>
              <Select
                value={filter.siteLevel.trial?.value ?? null}
                name="siteLevelTrial"
                placeholder={t('ETMF_LABEL_SELECT_STUDY')}
                onChange={(option: any) => {
                  const filterData = { ...filter }
                  filterData.siteLevel.trial = option
                  filterData.siteLevel.environment = null
                  filterData.siteLevel.country = null
                  filterData.siteLevel.site = null
                  filterData.siteLevel.tag = null
                  filterData.siteLevel.milestone = null
                  filterData.siteLevel.stage = null
                  filterData.siteLevel.rule = null
                  setFilter(filterData)
                  onFilterChange('siteLevel', 'trial')
                }}
                options={
                  filteredSelectsOptions.siteLevel.trial?.map((t) => ({
                    value: t.fsiId,
                    label: t.fsiName || '',
                    fsiId: t.fsiId,
                    fsiName: t.fsiName
                  })) ?? []
                }
                isDisabled={!filter.siteLevel.product || !filteredSelectsOptions.siteLevel.trial?.length}
              />
            </div>

            {/* Environment Filter */}
            <div
              className={`form-group w100p mb7 ${!filter.siteLevel.trial || !filteredSelectsOptions.siteLevel.environment?.length ? 'disabled' : ''} ${!properties.milestone?.id ? 'mb0' : 'mb7'}`}
            >
              <ControlLabel className="with-asterisk">{t('EMTF_MD_ENVIRONMENT')}</ControlLabel>
              <Select
                value={filter.siteLevel.environment?.value ?? null}
                name="siteLevelEnvironment"
                placeholder={t('SELECT_ENVIRONMENT')}
                onChange={(option: any) => {
                  const filterData = { ...filter }
                  filterData.siteLevel.environment = option
                  filterData.siteLevel.country = null
                  filterData.siteLevel.site = null
                  filterData.siteLevel.tag = null
                  filterData.siteLevel.milestone = null
                  filterData.siteLevel.stage = null
                  filterData.siteLevel.rule = null
                  setFilter(filterData)
                  onFilterChange('siteLevel', 'environment')
                }}
                options={
                  filteredSelectsOptions.siteLevel.environment?.map((e) => ({
                    value: e.fsiId,
                    label: e.fsiName || '',
                    fsiId: e.fsiId,
                    fsiName: e.fsiName
                  })) ?? []
                }
                isDisabled={!filter.siteLevel.trial || !filteredSelectsOptions.siteLevel.environment?.length}
              />
            </div>

            {/* Country Filter */}
            <div
              className={`form-group w100p mb7 ${!filter.siteLevel.environment || !filteredSelectsOptions.siteLevel.country?.length ? 'disabled' : ''} ${!properties.milestone?.id ? 'mb0' : 'mb7'}`}
            >
              <ControlLabel className="with-asterisk">{t('EMTF_MD_COUNTRY')}</ControlLabel>
              <Select
                value={filter.siteLevel.country?.value ?? null}
                name="siteLevelCountry"
                placeholder={t('ETMF_LABEL_SELECT_COUNTRY')}
                onChange={(option: any) => {
                  const filterData = { ...filter }
                  filterData.siteLevel.country = option
                  filterData.siteLevel.site = null
                  filterData.siteLevel.tag = null
                  filterData.siteLevel.milestone = null
                  filterData.siteLevel.stage = null
                  filterData.siteLevel.rule = null
                  setFilter(filterData)
                  onFilterChange('siteLevel', 'country')
                }}
                options={
                  filteredSelectsOptions.siteLevel.country?.map((c) => ({
                    value: c.fsiId,
                    label: c.fsiName || '',
                    fsiId: c.fsiId,
                    fsiName: c.fsiName
                  })) ?? []
                }
                isDisabled={!filter.siteLevel.environment || !filteredSelectsOptions.siteLevel.country?.length}
              />
            </div>

            {/* Site Filter */}
            <div
              className={`form-group w100p mb7 ${!filter.siteLevel.country || !filteredSelectsOptions.siteLevel.site?.length ? 'disabled' : ''} ${!properties.milestone?.id ? 'mb0' : 'mb7'}`}
            >
              <ControlLabel className="with-asterisk">{t('EMTF_MD_SITE')}</ControlLabel>
              <div className="select-with-btn-wrapper etmf-overview-new">
                <Select
                  value={filter.siteLevel.site?.value ?? null}
                  name="siteLevelSite"
                  placeholder={t('SELECT_SITE')}
                  onChange={(option: any) => {
                    const filterData = { ...filter }
                    filterData.siteLevel.site = option
                    filterData.siteLevel.tag = null
                    filterData.siteLevel.milestone = null
                    filterData.siteLevel.stage = null
                    filterData.siteLevel.rule = null
                    setFilter(filterData)
                    onFilterChange('siteLevel', 'site')
                  }}
                  options={[
                    ...(filteredSelectsOptions.siteLevel.site?.length > 1
                      ? [
                          {
                            value: 'all',
                            label: t('ALL_SITES') || 'All sites',
                            fsiId: 'all',
                            fsiName: t('ALL_SITES') || 'All sites'
                          }
                        ]
                      : []),
                    ...(filteredSelectsOptions.siteLevel.site?.map((s) => ({
                      value: s.fsiId,
                      label: s.fsiName || '',
                      fsiId: s.fsiId,
                      fsiName: s.fsiName
                    })) ?? [])
                  ]}
                  isDisabled={!filter.siteLevel.country || !filteredSelectsOptions.siteLevel.site?.length}
                />
                <button
                  type="button"
                  className={`goto-folder react-version-cta ${
                    !canGoToFolder('siteLevel', Constants.etmf.masterData.type.SITE) ? 'disabled' : ''
                  }`}
                  onClick={() => goToFolder(Constants.etmf.masterData.type.SITE, filter.siteLevel)}
                  disabled={!canGoToFolder('siteLevel', Constants.etmf.masterData.type.SITE)}
                  title={t('ETMF_TOOLTIP_GO_TO_PARENT_FOLDER').replace(/<[^>]*>/g, '')}
                />
              </div>
            </div>

            {/* Tag Filter */}
            <div
              className={`form-group w100p mb7 ${!filter.siteLevel.site || !filteredSelectsOptions.siteLevel.tag?.length ? 'disabled' : ''}`}
            >
              <ControlLabel>{t('TAG_FILTER')}</ControlLabel>
              <Select
                value={filter.siteLevel.tag?.value ?? null}
                name="siteLevelTag"
                placeholder={t('SELECT_TAG')}
                onChange={(option: any) => {
                  const filterData = { ...filter }
                  filterData.siteLevel.tag = option
                  setFilter(filterData)
                  onFilterChange('siteLevel', 'tag')
                }}
                options={
                  filteredSelectsOptions.siteLevel.tag?.map((tag) => ({ value: tag.id, label: tag.name || '' })) ?? []
                }
                isDisabled={!filter.siteLevel.site || !filteredSelectsOptions.siteLevel.tag?.length}
              />
            </div>

            {/* Milestone Filter (conditional) */}
            {!!properties.milestone?.id && (
              <div
                className={`form-group w100p mb7 ${!filter.siteLevel.site || !properties.milestone?.usedValues?.length ? 'disabled' : ''}`}
              >
                <ControlLabel>{t('ETMF_OVERVIEW_MILESTONE')}</ControlLabel>
                <Select
                  value={filter.siteLevel.milestone?.value ?? null}
                  name="siteLevelMilestone"
                  placeholder={t('ETMF_LABEL_SELECT_MILESTONE')}
                  onChange={(option: any) => {
                    const filterData = { ...filter }
                    filterData.siteLevel.milestone = option
                    setFilter(filterData)
                    onFilterChange('siteLevel', 'milestone')
                  }}
                  options={
                    properties.milestone?.usedValues?.map((item: any, index: number) => ({
                      value: index + 1,
                      label: item
                    })) ?? []
                  }
                  isDisabled={!filter.siteLevel.site || !properties.milestone?.usedValues?.length}
                />
              </div>
            )}

            {/* Stage Filter (conditional) */}
            {!!properties.stage?.id && (
              <div
                className={`form-group w100p mb7 ${!filter.siteLevel.site || !properties.stage?.usedValues?.length ? 'disabled' : ''}`}
              >
                <ControlLabel>{t('ETMF_LABEL_SELECT_STAGE')}</ControlLabel>
                <Select
                  value={filter.siteLevel.stage?.value ?? null}
                  name="siteLevelStage"
                  placeholder={t('ETMF_LABEL_SELECT_STAGE')}
                  onChange={(option: any) => {
                    const filterData = { ...filter }
                    filterData.siteLevel.stage = option
                    setFilter(filterData)
                    onFilterChange('siteLevel', 'stage')
                  }}
                  options={
                    properties.stage?.usedValues?.map((item: any, index: number) => ({
                      value: index + 1,
                      label: item
                    })) ?? []
                  }
                  isDisabled={!filter.siteLevel.site || !properties.stage?.usedValues?.length}
                />
              </div>
            )}

            {/* Rule Filter (conditional) */}
            {!!properties.rule?.id && (
              <div
                className={`form-group w100p mb7 ${!filter.siteLevel.site || !properties.rule?.usedValues?.length ? 'disabled' : ''}`}
              >
                <ControlLabel>{t('ETMF_LABEL_SELECT_RULE')}</ControlLabel>
                <Select
                  value={filter.siteLevel.rule?.value ?? null}
                  name="siteLevelRule"
                  placeholder={t('ETMF_LABEL_SELECT_RULE')}
                  onChange={(option: any) => {
                    const filterData = { ...filter }
                    filterData.siteLevel.rule = option
                    setFilter(filterData)
                    onFilterChange('siteLevel', 'rule')
                  }}
                  options={
                    properties.rule?.usedValues?.map((item: any, index: number) => ({
                      value: index + 1,
                      label: item
                    })) ?? []
                  }
                  isDisabled={!filter.siteLevel.site || !properties.rule?.usedValues?.length}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Study Documents Table Section */}
      <div className="study-documents-section mt-4">
        <StudyDocuments filter={studyDocumentsFilter} />
      </div>
    </div>
  )
}

export default Status
